import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Subject, takeUntil } from 'rxjs';
import { RtI18nDirective } from '../../runtime-translation.directive';
import { SchoolWebsiteService } from '../../school-website.service';
import { SchoolTourService } from '../../school-tour.service';
import { PortalLinkService } from '../../../../../services/portal-link.service';
import { StudentInSchoolRecord } from '../../../student/models/StudentInSchoolRecord';

export interface RegistrationCompleteDialogData {
  studentFirstName: string;
  record: StudentInSchoolRecord;
}

/**
 * "Registration is complete" dialog, opened from the green enrollment stage card's
 * "View Next Steps" (mirrors the portal's registration-complete dialog). Purely
 * informational — nothing is saved on open or close. Three next-step tiles:
 *  1. Additional forms — deep link to the portal's non-required forms tab.
 *  2. School website — CMS lookup by the record's SIS school ID; within 14 days of the
 *     first day of school it targets the school's first-day page instead. Disabled when
 *     the CMS has no site for the school.
 *  3. Schedule a tour — the school's TourSchoolLink from the same CMS raw-content source;
 *     disabled when the school hasn't configured one (matches the portal).
 */
@Component({
  selector: 'app-registration-complete-modal',
  standalone: true,
  templateUrl: './registration-complete-modal.component.html',
  styleUrl: './registration-complete-modal.component.scss',
  imports: [MatDialogModule, RtI18nDirective]
})
export class RegistrationCompleteModalComponent implements OnInit, OnDestroy {
  websiteUrl: string | null = null;
  tourUrl: string | null = null;

  private destroy$ = new Subject<void>();

  constructor(
    private dialogRef: MatDialogRef<RegistrationCompleteModalComponent>,
    private schoolWebsiteService: SchoolWebsiteService,
    private schoolTourService: SchoolTourService,
    private portalLink: PortalLinkService,
    @Inject(MAT_DIALOG_DATA) public data: RegistrationCompleteDialogData
  ) {}

  get record(): StudentInSchoolRecord {
    return this.data.record;
  }

  ngOnInit(): void {
    // record.SchoolID is the NHA/SIS school ID — the same key the CMS uses.
    this.schoolWebsiteService.getSchoolWebsiteUrl(this.record.SchoolID).pipe(
      takeUntil(this.destroy$)
    ).subscribe(url => {
      this.websiteUrl = url ? this.applyFirstDayDeepLink(url) : null;
    });

    this.schoolTourService.getTourLink(this.record.SchoolID).pipe(
      takeUntil(this.destroy$)
    ).subscribe(url => {
      this.tourUrl = url;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Within 14 days of the school's first day, the website button targets the school's
   * first-day-of-school page instead of its home page (matches the portal).
   */
  private applyFirstDayDeepLink(websiteUrl: string): string {
    const firstDay = new Date(this.record.FirstDayOfSchool).getTime();
    const daysUntil = (firstDay - Date.now()) / 86_400_000;
    return daysUntil >= 0 && daysUntil <= 14
      ? `${websiteUrl.replace(/\/$/, '')}/first-day-of-school`
      : websiteUrl;
  }

  /** The portal's non-required forms tab (includes the volunteer sign-up). */
  openAdditionalForms(): void {
    this.portalLink.open(`student/${this.record.StudentID}/${this.record.StudentInSchoolID}/forms/additional`);
    this.dialogRef.close();
  }

  openWebsite(): void {
    if (this.websiteUrl) {
      window.open(this.websiteUrl, '_blank');
    }
  }

  openTour(): void {
    if (this.tourUrl) {
      window.open(this.tourUrl, '_blank');
    }
  }

  close(): void {
    this.dialogRef.close();
  }
}
